import { Controller, UseFormReturn } from "react-hook-form";
import { PriceFormModel } from "@/modules/organization/shared/models/price";
import { FormControlLabel, Grid } from "@mui/material";
import { EvesTextbox } from "@/common/components/atoms/textbox/textbox";
import { EvesSwitch } from "@/common/components/atoms/switch/switch";
import { EvesCheckbox } from "@/common/components/atoms/checkbox/checkbox";
import { useTranslation } from "react-i18next";

interface IDimensionPropsModel {
  priceForm: UseFormReturn<PriceFormModel, any>;
}

export default function Dimension(props: IDimensionPropsModel) {
  const { t: translate } = useTranslation();

  const { control, formState, watch, setValue } = props.priceForm;
  const { errors } = formState;

  const flatFeeActive = watch("dimensions.flatFee.active");
  const energyActive = watch("dimensions.energy.active");
  const energyStepSizeEnabled =
    watch("dimensions.energy.stepSizeEnabled") && energyActive;
  const chargingActive = watch("dimensions.chargingTime.active");
  const chargingStepSizeEnabled =
    watch("dimensions.chargingTime.stepSizeEnabled") && chargingActive;
  const parkingActive = watch("dimensions.parkingTime.active");
  const parkingStepSizeEnabled =
    watch("dimensions.parkingTime.stepSizeEnabled") && parkingActive;

  return (
    <>
      {/* Connection Fee */}
      <Grid
        container
        direction="row"
        display={"flex"}
        alignContent={"center"}
        sx={{
          mb: 2,
          backgroundColor: "#fff",
          padding: 2,
          borderRadius: "8px",
          border: "1px solid #E6E6E6",
        }}
      >
        <Grid item sm={4}>
          <>
            <Controller
              name="dimensions.flatFee.active"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <EvesSwitch
                      {...field}
                      checked={field.value}
                      id="flatFeeActive"
                    />
                  }
                  label={translate("settings.pricing.flat_fee_dimension")}
                />
              )}
            />
            {flatFeeActive && (
              <Controller
                name="dimensions.flatFee.price"
                control={control}
                render={({ field }) => (
                  <EvesTextbox
                    {...field}
                    id="flatFeePrice"
                    //label={currencySymbol + "/session"}
                    fullWidth
                    required={flatFeeActive}
                    variant="standard"
                    disabled={!flatFeeActive}
                    error={
                      flatFeeActive && !!errors?.dimensions?.flatFee?.price
                    }
                    helperText={
                      flatFeeActive &&
                      `${translate(
                        errors?.dimensions?.flatFee?.price?.message || " "
                      )}`
                    }
                  />
                )}
              />
            )}
          </>
        </Grid>
      </Grid>

      {/* Energy */}
      <Grid
        container
        direction="row"
        display={"flex"}
        alignContent={"center"}
        sx={{
          mb: 2,
          backgroundColor: "#fff",
          padding: 2,
          borderRadius: "8px",
          border: "1px solid #E6E6E6",
        }}
      >
        <Grid item sm={3}>
          <>
            <Controller
              name="dimensions.energy.active"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <EvesSwitch
                      {...field}
                      checked={field.value}
                      id="energyActive"
                      onChange={(e) => {
                        field.onChange(e.target.checked);
                        if (e.target.checked) {
                          setValue("dimensions.chargingTime.active", false);
                        }
                      }}
                    />
                  }
                  label={translate("settings.pricing.energy_dimension")}
                />
              )}
            />
            {energyActive && (
              <Controller
                name="dimensions.energy.price"
                control={control}
                render={({ field }) => (
                  <EvesTextbox
                    {...field}
                    id="energyPrice"
                    // label={currencySymbol + "/kWh"}
                    fullWidth
                    required={energyActive}
                    variant="standard"
                    disabled={!energyActive}
                    error={energyActive && !!errors?.dimensions?.energy?.price}
                    helperText={
                      energyActive &&
                      `${translate(
                        errors?.dimensions?.energy?.price?.message || " "
                      )}`
                    }
                  />
                )}
              />
            )}
          </>
        </Grid>
        <Grid item sm={3} sx={{ px: 2 }} />
        <Grid
          item
          sm={3}
          display={"flex"}
          justifyContent={"center"}
          flexDirection={"column"}
        >
          {energyActive && (
            <>
              <Controller
                name="dimensions.energy.stepSizeEnabled"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <EvesCheckbox
                        {...field}
                        checked={field.value}
                        id="energyStepSizeActive"
                        disabled={!energyActive}
                      />
                    }
                    label={translate("settings.pricing.energy_step_unit")}
                  />
                )}
              />
              {energyStepSizeEnabled && (
                <Controller
                  name="dimensions.energy.stepSize"
                  control={control}
                  render={({ field }) => (
                    <EvesTextbox
                      {...field}
                      id="energyStepSize"
                      //label={translate("settings.pricing.energy_step_unit")}
                      fullWidth
                      required={energyStepSizeEnabled}
                      variant="standard"
                      disabled={!energyStepSizeEnabled}
                      error={
                        energyStepSizeEnabled &&
                        !!errors?.dimensions?.energy?.stepSize
                      }
                      helperText={
                        energyStepSizeEnabled &&
                        `${translate(
                          errors?.dimensions?.energy?.stepSize?.message || " "
                        )}`
                      }
                    />
                  )}
                />
              )}
            </>
          )}
        </Grid>
        <Grid item sm={3} />
      </Grid>

      {/* Charging Time */}
      <Grid
        container
        direction="row"
        display={"flex"}
        alignContent={"center"}
        sx={{
          mb: 2,
          backgroundColor: "#fff",
          padding: 2,
          borderRadius: "8px",
          border: "1px solid #E6E6E6",
        }}
      >
        <Grid item sm={3}>
          <>
            <Controller
              name="dimensions.chargingTime.active"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <EvesSwitch
                      {...field}
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e.target.checked);
                        if (e.target.checked) {
                          setValue("dimensions.energy.active", false);
                        }
                      }}
                      id="chargingActive"
                    />
                  }
                  label={translate("settings.pricing.charging_time_dimension")}
                />
              )}
            />
            {chargingActive && (
              <Controller
                name="dimensions.chargingTime.price"
                control={control}
                render={({ field }) => (
                  <EvesTextbox
                    {...field}
                    id="chargingPrice"
                    //label={currencySymbol + "/hour"}
                    fullWidth
                    required={chargingActive}
                    variant="standard"
                    disabled={!chargingActive}
                    error={
                      chargingActive &&
                      !!errors?.dimensions?.chargingTime?.price
                    }
                    helperText={
                      chargingActive &&
                      `${translate(
                        errors?.dimensions?.chargingTime?.price?.message || " "
                      )}`
                    }
                  />
                )}
              />
            )}
          </>
        </Grid>
        <Grid item sm={3} sx={{ px: 2 }} />
        <Grid
          item
          sm={3}
          display={"flex"}
          justifyContent={"center"}
          flexDirection={"column"}
        >
          {chargingActive && (
            <>
              <Controller
                name="dimensions.chargingTime.stepSizeEnabled"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <EvesCheckbox
                        {...field}
                        checked={field.value}
                        id="chargingStepSizeActive"
                        disabled={!chargingActive}
                      />
                    }
                    label={translate("settings.pricing.time_step_unit")}
                  />
                )}
              />
              {chargingStepSizeEnabled && (
                <Controller
                  name="dimensions.chargingTime.stepSize"
                  control={control}
                  render={({ field }) => (
                    <EvesTextbox
                      {...field}
                      id="chargingStepSize"
                      //label={translate("settings.pricing.time_step_unit")}
                      fullWidth
                      required={chargingStepSizeEnabled}
                      variant="standard"
                      disabled={!chargingStepSizeEnabled}
                      error={
                        chargingStepSizeEnabled &&
                        !!errors?.dimensions?.chargingTime?.stepSize
                      }
                      helperText={
                        chargingStepSizeEnabled &&
                        `${translate(
                          errors?.dimensions?.chargingTime?.stepSize?.message ||
                            " "
                        )}`
                      }
                    />
                  )}
                />
              )}
            </>
          )}
        </Grid>
        <Grid item sm={3} />
      </Grid>

      {/* Idle Fee */}
      <Grid
        container
        direction="row"
        display={"flex"}
        alignContent={"center"}
        sx={{
          mb: 2,
          backgroundColor: "#fff",
          padding: 2,
          borderRadius: "8px",
          border: "1px solid #E6E6E6",
        }}
      >
        <Grid item sm={3}>
          <>
            <Controller
              name="dimensions.parkingTime.active"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <EvesSwitch
                      {...field}
                      checked={field.value}
                      id="parkingActive"
                    />
                  }
                  label={translate("settings.pricing.parking_time_dimension")}
                />
              )}
            />
            {parkingActive && (
              <Controller
                name="dimensions.parkingTime.price"
                control={control}
                render={({ field }) => (
                  <EvesTextbox
                    {...field}
                    id="parkingPrice"
                    //label={currencySymbol + "/hour"}
                    fullWidth
                    required={parkingActive}
                    variant="standard"
                    disabled={!parkingActive}
                    error={
                      parkingActive && !!errors?.dimensions?.parkingTime?.price
                    }
                    helperText={
                      parkingActive &&
                      `${translate(
                        errors?.dimensions?.parkingTime?.price?.message || " "
                      )}`
                    }
                  />
                )}
              />
            )}
          </>
        </Grid>
        <Grid item sm={3} sx={{ px: 2 }}>
          {parkingActive && (
            <Controller
              name="dimensions.parkingTime.gracePeriod"
              control={control}
              render={({ field }) => (
                <EvesTextbox
                  {...field}
                  id="gracePeriod"
                  label={translate("settings.pricing.grace_period")}
                  fullWidth
                  type="number"
                  sx={{ mt: "20px" }}
                  InputProps={{ inputProps: { min: 0, max: 60 } }}
                  required={parkingActive}
                  variant="standard"
                  disabled={!parkingActive}
                  error={
                    parkingActive &&
                    !!errors?.dimensions?.parkingTime?.gracePeriod
                  }
                  helperText={
                    parkingActive &&
                    `${translate(
                      errors?.dimensions?.parkingTime?.gracePeriod?.message ||
                        " "
                    )}`
                  }
                />
              )}
            />
          )}
        </Grid>
        <Grid
          item
          sm={3}
          display={"flex"}
          justifyContent={"center"}
          flexDirection={"column"}
        >
          {parkingActive && (
            <>
              <Controller
                name="dimensions.parkingTime.stepSizeEnabled"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <EvesCheckbox
                        {...field}
                        checked={field.value}
                        id="parkingStepSizeActive"
                        disabled={!parkingActive}
                      />
                    }
                    label={translate("settings.pricing.time_step_unit")}
                  />
                )}
              />
              {parkingStepSizeEnabled && (
                <Controller
                  name="dimensions.parkingTime.stepSize"
                  control={control}
                  render={({ field }) => (
                    <EvesTextbox
                      {...field}
                      id="parkingStepSize"
                      //label={translate("settings.pricing.time_step_unit")}
                      fullWidth
                      required={parkingStepSizeEnabled}
                      variant="standard"
                      disabled={!parkingStepSizeEnabled}
                      error={
                        parkingStepSizeEnabled &&
                        !!errors?.dimensions?.parkingTime?.stepSize
                      }
                      helperText={
                        parkingStepSizeEnabled &&
                        `${translate(
                          errors?.dimensions?.parkingTime?.stepSize?.message ||
                            " "
                        )}`
                      }
                    />
                  )}
                />
              )}
            </>
          )}
        </Grid>
        <Grid item sm={3} />
      </Grid>
    </>
  );
}
